'use client'

import React, { useState } from 'react'
import { Box, Button, Card, Flex, Skeleton, Text, Title, ActionIcon } from '@mantine/core'
import { IconPlus, IconCheck } from '@tabler/icons-react'
import { ServiceItemCardDirection } from '@/types/enums/ServiceItem'
import { ServiceItem } from '@/types/interfaces/service-item'
import { ServiceItemCardBadge } from '@/components/ServiceItemCardBadge'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import GradientSvgIcon from './GradientSvgIcon'
import Image from 'next/image'
import { useGetVisitCartQuery, useAddToCartMutation, useRemoveFromCartMutation } from '@/lib/store/api/visitCartApi'
import BookVisitDrawer from '@/components/GeneralAi/BookVisitDrawer'
import useNotifications from '@/hooks/useNotifications'
import { trackEvent, PageLocation, StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { isInPersonModality } from '@/lib/sku/inPersonVisitRollout'
import { useUserContext } from '@/contexts/UserContext'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import { isPrescriptionRenewalStorefrontSkuId } from '@/types/enums/sku-constants'

interface Props {
    service: ServiceItem | undefined
    showDescription?: boolean
    direction?: ServiceItemCardDirection
    pageLocation: PageLocation
    additionalInformation?: Record<string, any>
}

const ServiceItemCard: React.FC<Props> = ({
    service,
    showDescription = false,
    direction = ServiceItemCardDirection.column,
    pageLocation,
    additionalInformation,
}) => {
    const router = useRouter()
    const isRow = direction === 'row'
    const [showBookDrawer, setShowBookDrawer] = useState(false)
    const isGeneralAiChat = pageLocation === PageLocation.GENERAL_AI_CHAT
    const { data: cartData } = useGetVisitCartQuery(undefined, {
        skip: !isGeneralAiChat,
    })
    const { user } = useUserContext()
    const isPrescriptionRenewal = isPrescriptionRenewalStorefrontSkuId(service?.skuId)
    const { href: prescriptionRenewalHref, isNavigating, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(!!user && isPrescriptionRenewal)
    const cartItems = cartData?.items ?? []
    const [addToCart] = useAddToCartMutation()
    const [removeFromCart] = useRemoveFromCartMutation()
    const showNotification = useNotifications()

    const isInCart =
        isGeneralAiChat && service && cartItems.some((item) => String(item.skuId) === String(service.skuId))

    const dragStartPos = React.useRef<{ x: number; y: number } | null>(null)

    if (!service) {
        return (
            <Card
                padding={0}
                display="flex"
                h="100%"
                bg="transparent"
                bd="none"
                shadow="none"
                withBorder={false}
                style={{
                    flexDirection: direction,
                    gap: isRow ? 24 : 0,
                    overflow: 'hidden',
                    border: 'none',
                }}
            >
                <Box
                    pos="relative"
                    style={{
                        aspectRatio: '1 / 1',
                        overflow: 'hidden',
                        borderRadius: '16px',
                        ...(isRow ? { height: '125px' } : { width: '100%' }),
                    }}
                >
                    <Skeleton height={isRow ? 120 : 160} radius="md" />
                </Box>
                <Flex direction="column" gap={8} flex={1} py={isRow ? 4 : 'xs'}>
                    <Skeleton height={20} width="90%" radius="sm" />
                    {showDescription && <Skeleton height={16} width="80%" radius="sm" />}
                    <Skeleton height={18} width="40%" radius="sm" />
                    <Skeleton height={24} width="30%" radius="sm" />
                </Flex>
            </Card>
        )
    }

    const svc = service as ServiceItem

    const isChatService = svc.originType === 'chat'

    const getSkuUrl = () => {
        const baseUrl = `/s/${svc.skuName}/${svc.skuId}`
        if (pageLocation === PageLocation.GENERAL_AI_CHAT) {
            return `${baseUrl}?ref=genai`
        }
        return baseUrl
    }
    const href = isPrescriptionRenewal ? prescriptionRenewalHref : getSkuUrl()

    const creamColor = { background: '#EBDACE' }

    const handlePointerDown = (e: React.PointerEvent) => {
        dragStartPos.current = { x: e.clientX, y: e.clientY }
    }

    const handleClick = async (e: React.MouseEvent) => {
        // If we detected a drag (movement > 5px), don't navigate
        if (dragStartPos.current) {
            const deltaX = Math.abs(e.clientX - dragStartPos.current.x)
            const deltaY = Math.abs(e.clientY - dragStartPos.current.y)
            if (deltaX > 5 || deltaY > 5) {
                e.preventDefault()
                dragStartPos.current = null
                return
            }
        }
        dragStartPos.current = null

        trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
            location: pageLocation,
            additionalInformation,
            sku: svc,
        })
        trackEvent('clicked_genai_sku_carousel_item', {
            sku_id: svc.skuId,
            sku_name: svc.skuName,
            sku_title: svc.title,
        })
        if (isPrescriptionRenewal) {
            await navigateToPrescriptionRenewal(e)
        }
    }

    const cardContent = (
        <>
            {renderImage()}
            {renderInfoBox()}
        </>
    )

    if (isGeneralAiChat) {
        return (
            <>
                <Card
                    padding={0}
                    display="flex"
                    h="100%"
                    bg="transparent"
                    bd="none"
                    shadow="none"
                    component="div"
                    style={{
                        flexDirection: direction,
                        justifyContent: 'space-between',
                        gap: isRow ? 24 : 0,
                        overflow: 'hidden',
                        width: '11.75rem',
                    }}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    data-testid={`service-item-card-${svc.skuName}`}
                >
                    <Link
                        href={href}
                        onClick={handleClick}
                        onPointerDown={handlePointerDown}
                        draggable={false}
                        aria-busy={isPrescriptionRenewal && isNavigating ? true : undefined}
                        style={{
                            textDecoration: 'none',
                            color: 'inherit',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                        }}
                    >
                        {renderImage()}
                        {renderClickableInfo()}
                    </Link>
                    {isChatService && renderBookButton()}
                </Card>
                <BookVisitDrawer opened={showBookDrawer} onClose={() => setShowBookDrawer(false)} service={service} />
            </>
        )
    }

    return (
        <Link
            href={href}
            data-testid={`service-item-card-${svc.skuName}`}
            draggable={false}
            aria-busy={isPrescriptionRenewal && isNavigating ? true : undefined}
            onClick={async (event) => {
                trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
                    location: pageLocation,
                    additionalInformation,
                    sku: svc,
                })
                if (isPrescriptionRenewal) {
                    await navigateToPrescriptionRenewal(event)
                }
            }}
        >
            <Card
                padding={0}
                display="flex"
                w="288px"
                h="432px"
                bg="transparent"
                bd="none"
                shadow="none"
                style={{
                    flexDirection: direction,
                    gap: isRow ? 24 : 0,
                    overflow: 'hidden',
                }}
            >
                {renderImage()}
                {renderInfoBox()}
            </Card>
        </Link>
    )

    function renderImage() {
        const url = String(svc.imageUrl ?? '')
        const isFallbackIcon = url.startsWith('fallback-icons/')
        const isPlaceholder = url.startsWith('fallback_images/')
        const cat = (svc.category ?? '').toLowerCase()
        const isLabOrMed = cat === 'lab' || cat === 'medication'
        const isSmall = isLabOrMed || isPlaceholder

        const wrapper: React.CSSProperties = {
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 16,
            ...creamColor,
            ...(isGeneralAiChat
                ? { width: '11.75rem', height: '7.0625rem', aspectRatio: 'unset', flexShrink: 0 }
                : {
                      aspectRatio: '1 / 1',
                      ...(isRow ? { width: 125, height: 125 } : { width: '100%' }),
                  }),
        }

        const centerContent: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }

        if (isFallbackIcon) {
            return (
                <Box style={{ ...wrapper, ...creamColor, ...centerContent }}>
                    <GradientSvgIcon src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${url}`} size={85} />
                </Box>
            )
        }

        if (isSmall) {
            return (
                <Box style={{ ...wrapper, ...creamColor, ...centerContent }}>
                    <Image
                        src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${url}`}
                        alt={svc.skuName ?? ''}
                        // width={70}
                        // height={85}
                        width={126}
                        height={153}
                        sizes="(max-width:768px) 85px, 125px"
                        style={{ objectFit: 'contain', background: '#EBDACE' }}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                    />
                </Box>
            )
        }

        return (
            <Box style={wrapper}>
                <Image
                    src={`https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${url}`}
                    alt={svc.skuName ?? ''}
                    fill
                    sizes={isRow ? '125px' : '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 290px'}
                    style={{
                        objectFit: 'cover',
                        objectPosition: 'center',
                        background: '#EBDACE',
                    }}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                />
            </Box>
        )
    }

    function renderInfoBox() {
        return (
            <Flex direction="column" gap={8} flex={1} py={isRow ? 4 : 'xs'} justify="space-between">
                <Flex direction="column" gap={8}>
                    {renderInfoBoxTitle()}
                    {showDescription && renderInfoBoxSubtitle()}
                    {renderInfoBoxCost()}
                    {renderInfoBoxBadge()}
                </Flex>
                {isChatService && renderBookButton()}
            </Flex>
        )
    }

    function renderClickableInfo() {
        return (
            <Flex direction="column" gap={8} flex={1} py={isRow ? 4 : 'xs'}>
                {renderInfoBoxTitle()}
                {showDescription && renderInfoBoxSubtitle()}
                {renderInfoBoxCost()}
                {renderInfoBoxBadge()}
            </Flex>
        )
    }

    function renderBookButton() {
        if (isGeneralAiChat) {
            return (
                <Flex gap="sm" align="center">
                    <Button
                        variant="filled"
                        size="md"
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            trackEvent('clicked_genai_sku_carousel_book')
                            setShowBookDrawer(true)
                        }}
                    >
                        Book
                    </Button>
                    <ActionIcon
                        variant="filled"
                        size="lg"
                        color={isInCart ? 'green.6' : 'gray'}
                        onClick={async (event) => {
                            event.preventDefault()
                            event.stopPropagation()

                            if (isInCart) {
                                // Item already in cart - remove it
                                try {
                                    await removeFromCart(service!.skuId).unwrap()
                                } catch (error) {
                                    console.error('Failed to remove from cart:', error)
                                }
                                return
                            }

                            trackEvent('clicked_genai_sku_carousel_add_to_visit')
                            try {
                                await addToCart(service!).unwrap()
                                // Successfully added - clear any limit flag
                                if (typeof window !== 'undefined') {
                                    window.dispatchEvent(new CustomEvent('visit-cart-limit-cleared'))
                                }
                            } catch (error: any) {
                                // Handle API errors
                                if (error?.data?.error === 'topic_limit_reached') {
                                    // Dispatch event to show MaxItemsBanner
                                    if (typeof window !== 'undefined') {
                                        window.dispatchEvent(new CustomEvent('visit-cart-limit-reached'))
                                    }
                                } else if (error?.data?.error === 'invalid_sku') {
                                    showNotification({
                                        title: 'Invalid service',
                                        message: 'This service is not available. Please try another one.',
                                        color: 'red',
                                    })
                                } else {
                                    console.error('Failed to add to cart:', error)
                                    showNotification({
                                        title: 'Error',
                                        message: 'Failed to add to visit. Please try again.',
                                        color: 'red',
                                    })
                                }
                            }
                        }}
                        style={
                            !isInCart
                                ? {
                                      backgroundColor: 'transparent',
                                      color: 'var(--gm-gray-9)',
                                      border: '1px solid var(--gm-gray-2)',
                                      borderRadius: '999px',
                                  }
                                : {
                                      backgroundColor: 'var(--gm-background-bg-positive-container)',
                                      color: 'var(--gm-green-7)',
                                  }
                        }
                    >
                        {isInCart ? <IconCheck size={18} /> : <IconPlus size={18} />}
                    </ActionIcon>
                </Flex>
            )
        }

        return (
            <Button
                variant="filled"
                size="sm"
                w="50%"
                mt="sm"
                onClick={(event) => {
                    event.preventDefault()
                    window.open(getSkuUrl(), '_blank')
                }}
            >
                Book
            </Button>
        )
    }
    function renderInfoBoxTitle() {
        return (
            <Title
                order={3}
                size={isRow ? '18px' : '16px'}
                c="black"
                fw={isRow ? 650 : 500}
                lineClamp={2}
                style={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}
            >
                {svc.title}
            </Title>
        )
    }

    function renderInfoBoxSubtitle() {
        return (
            <Text size="sm" c="dimmed" fz={14} fw={500} lh="130%" lineClamp={2}>
                {svc.description}
            </Text>
        )
    }

    function renderInfoBoxCost() {
        return svc.primaryPrice ? (
            <Flex gap={4} direction="column">
                <Flex justify="flex-start" align="flex-end" gap={4}>
                    <Text size="14px" fw={500} lh="140%" c="var(--gm-foreground-fg-primary)">
                        {svc.primaryPrice}
                    </Text>
                </Flex>

                <Text size="14px" fw={500} lh="140%" c="var(--gm-foreground-fg-tertiary)">
                    {svc.secondaryPrice}
                </Text>
            </Flex>
        ) : null
    }

    function renderInfoBoxBadge() {
        const inPersonAvailable = svc.price?.options?.some((option) => isInPersonModality(option.type)) ?? false
        return (
            <ServiceItemCardBadge
                category={svc.category}
                label={inPersonAvailable ? 'In-person visit' : undefined}
                mt={2}
            />
        )
    }
}

export default ServiceItemCard
